import { useEffect, useRef, useState } from "react";
import {
  ApiMessageHandler,
  ArtAlert,
  ArtButton,
  ArtTable,
  ArtTableHandle,
  HeadCell,
  SSRTableBuilder,
  useIdpService,
} from "@art/shared-ui";
import { Box, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

interface Role {
  id: number;
  name: string;
  description: string;
}

interface ManageRolesDialogProps {
  userId: string | number;
  onSaved: () => void;
  closeDialog: () => void;
}

export default function ManageRolesDialog({
  userId,
  onSaved,
  closeDialog,
}: ManageRolesDialogProps) {
  const tableRef = useRef<ArtTableHandle>(null);
  const idpService = useIdpService();
  const tenantName = useSelector((s: RootState) => s.auth.tenantName);

  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [assignedRoleIds, setAssignedRoleIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!tenantName || !userId) return;
      setLoading(true);
      setLoadError("");
      try {
        const [allRes, assignedRes] = await Promise.all([
          idpService.get(`api/tenants/${tenantName}/roles`),
          idpService.get(`api/tenants/${tenantName}/users/${userId}/roles`),
        ]);
        if (cancelled) return;
        setAllRoles(allRes?.data?.data?.role || []);
        const assigned = (assignedRes?.data?.data || []) as Role[];
        setAssignedRoleIds(assigned.map((r) => r.id));
      } catch (err) {
        if (!cancelled) {
          setLoadError(ApiMessageHandler.extractErrorMessage(err));
          setAllRoles([]);
          setAssignedRoleIds([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [idpService, tenantName, userId, loadAttempt]);

  const headCells: HeadCell<Role>[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Role",
      getColData: (row) => row.name,
    },
    {
      id: "description",
      numeric: false,
      disablePadding: false,
      label: "Description",
      getColData: (row) => row.description,
    },
  ];

  const tableBuilder = SSRTableBuilder<Role>({
    endpoint: "skip",
    onResponse: () => ({
      data: allRoles,
      total: allRoles.length,
    }),
  });

  const handleSave = async (selectedIds: number[]) => {
    if (!tenantName || saving) return;

    const added = selectedIds.filter((id) => !assignedRoleIds.includes(id));
    const removed = assignedRoleIds.filter((id) => !selectedIds.includes(id));

    if (added.length === 0 && removed.length === 0) {
      closeDialog();
      return;
    }

    setSaving(true);
    setSaveError("");
    try {
      const requests: Promise<unknown>[] = [];
      if (added.length > 0) {
        requests.push(
          idpService.post(`api/tenants/${tenantName}/users/${userId}/roles`, {
            role_ids: added,
          }),
        );
      }
      if (removed.length > 0) {
        requests.push(
          idpService.delete(
            `api/tenants/${tenantName}/users/${userId}/roles`,
            { data: { role_ids: removed } },
          ),
        );
      }
      await Promise.all(requests);
      onSaved();
      closeDialog();
    } catch (err) {
      setSaveError(ApiMessageHandler.extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box>
        <ArtAlert severity="error" sx={{ mb: 2 }}>{loadError}</ArtAlert>
        <ArtButton label="Retry" onClick={() => setLoadAttempt((attempt) => attempt + 1)} />
      </Box>
    );
  }

  if (allRoles.length === 0) {
    return (
      <Box py={4} textAlign="center" color="text.secondary">
        No roles available to assign.
      </Box>
    );
  }

  return (
    <Box className="non-clickable-table">
      {saveError && <ArtAlert severity="error" sx={{ mb: 2 }}>{saveError}</ArtAlert>}
      <ArtTable
        ref={tableRef}
        headCells={headCells}
        builder={tableBuilder}
        search={{ enabled: true, value: "" }}
        disablePagination={true}
        bulkOperationActions={{
          selected: assignedRoleIds,
          findDiff: (selected: number[]) => {
            const added = selected.filter((id) => !assignedRoleIds.includes(id));
            const removed = assignedRoleIds.filter((id) => !selected.includes(id));
            return { added: added.length, removed: removed.length };
          },
          actions: (selected: number[]) => {
            const added = selected.filter((id) => !assignedRoleIds.includes(id));
            const removed = assignedRoleIds.filter((id) => !selected.includes(id));
            if (added.length === 0 && removed.length === 0) return [];
            return [
              <ArtButton
                dataQa="btn-save-user-roles"
                label={saving ? "Saving..." : "Save"}
                disabled={saving}
                onClick={() => handleSave(selected)}
              />,
            ];
          },
        }}
      />
    </Box>
  );
}
